/**
 * Module-specific copy for ChatInterface empty state and composer.
 * Does not affect API payloads or orchestration.
 */
import { PRODUCT_NAMES, VALIDATION_LIMITS } from './validationLimits';

export const MODULE_TYPES = {
  TECH_DOC: 'tech-doc',
  BRD: 'brd',
};

const SHARED_INTRO_TITLE =
  'Hello! I’m your intelligent documentation assistant. I can help you transform your inputs into structured, high-quality documents.';

const SHARED_INTRO_DESCRIPTION = 'Here’s what I can do for you:';

const SHARED_EMPTY_STATE =
  'Get started by attaching a file to generate your document.';

export const CHAT_MODULE_PRESETS = {
  [MODULE_TYPES.TECH_DOC]: {
    moduleType: MODULE_TYPES.TECH_DOC,
    pageTitle: 'Intelligent Documentation Suite',
    introTitle: SHARED_INTRO_TITLE,
    introDescription: SHARED_INTRO_DESCRIPTION,
    capabilitiesSectionTitle: '📄 Document Generation',
    capabilities: [
      'Generate Technical Documentation.',
      'Produce Architecture documentation.',
      'Generate Functional Documentation.',
      'Generate Data model diagram.',
      'Generate Database Schema Report.',
      'Generate Reverse Engineering Summary.',
    ],
    emptyStateMessage: SHARED_EMPTY_STATE,
    composerPlaceholder: null,
    uploadHints: [
      `Upload a project as a .zip (max ${VALIDATION_LIMITS.maxZipSizeMb} MB, up to ${VALIDATION_LIMITS.maxZipFileCount} files inside the archive).`,
      `Total source-code lines across the extracted project must not exceed ${VALIDATION_LIMITS.techDocMaxSourceLines.toLocaleString()} (summed across all code files).`,
      `You can run up to ${VALIDATION_LIMITS.techDocMaxGenerationsPerUser} successful generations per signed-in account.`,
    ],
    validationProductName: PRODUCT_NAMES.TECH_DOC,
  },
  [MODULE_TYPES.BRD]: {
    moduleType: MODULE_TYPES.BRD,
    pageTitle: 'BRD for Oracle Forms',
    introTitle: 'Hello! I’m your BRD documentation assistant.',
    introDescription:
      'I can help you generate Business Requirements Documents for Oracle Forms modernization projects.',
    capabilitiesSectionTitle: '📄 BRD Generation',
    capabilities: [
      'Create Business Requirements Documents for Oracle Forms(BRD).',
    ],
    emptyStateMessage: SHARED_EMPTY_STATE,
    composerPlaceholder: null,
    uploadHints: [],
    validationProductName: PRODUCT_NAMES.BRD,
  },
};

/**
 * Merge explicit props with a module preset. Unknown moduleType falls back to tech-doc.
 */
export function resolveChatModuleConfig(props = {}) {
  const {
    moduleType = MODULE_TYPES.TECH_DOC,
    pageTitle,
    introTitle,
    introDescription,
    capabilities,
    capabilitiesSectionTitle,
    composerPlaceholder,
    emptyStateMessage,
    uploadHints,
    validationProductName,
  } = props;

  const preset =
    CHAT_MODULE_PRESETS[moduleType] || CHAT_MODULE_PRESETS[MODULE_TYPES.TECH_DOC];

  return {
    moduleType: preset.moduleType,
    pageTitle: pageTitle ?? preset.pageTitle,
    introTitle: introTitle ?? preset.introTitle,
    introDescription: introDescription ?? preset.introDescription,
    capabilitiesSectionTitle:
      capabilitiesSectionTitle ?? preset.capabilitiesSectionTitle,
    capabilities: capabilities ?? preset.capabilities,
    emptyStateMessage: emptyStateMessage ?? preset.emptyStateMessage,
    composerPlaceholder:
      composerPlaceholder !== undefined
        ? composerPlaceholder
        : preset.composerPlaceholder,
    uploadHints: uploadHints ?? preset.uploadHints ?? [],
    validationProductName: validationProductName ?? preset.validationProductName ?? PRODUCT_NAMES.TECH_DOC,
  };
}
